import type {
  AdditionalOccupant,
  FormProspect,
  PetOccupant,
  Prospect,
  ProspectPreferences,
  ProspectStatusType as ProspectStatus,
} from 'src/containers/ProspectProfile/types';
import { concat, isEmpty, isNil, map, omit, pickAll } from 'ramda';
import { NO_PREFERENCE_KEY, NO_PREFERENCE_TEXT } from './noPreference';

const DEFAULT_EMAIL_TYPE = 'personal';
const DEFAULT_PHONE_TYPE = 'mobile';

const setNullToEmpty = (x) => (isEmpty(x) && !Array.isArray(x) ? null : x);

export const isNBedsArrEmpty = (nBedsArr: string[]) =>
  Array.isArray(nBedsArr) && nBedsArr.length === 0;

export const nBedsArrIsEmptyArrayOrNull = (nBedsArr: string[]) =>
  isNBedsArrEmpty(nBedsArr) || isNil(nBedsArr);

export const nBedsExist = (nBeds: number) => !isNil(nBeds);

const isNoPreferenceSelectedForNumBeds = (nBedsArr: string[]) =>
  isNil(nBedsArr) ||
  (Array.isArray(nBedsArr) && nBedsArr[0] === NO_PREFERENCE_KEY);

const mapNBedsToDescription = (nBeds: string[]) => {
  const nBedsTextMap = {
    0: 'Studio',
    1: '1 Bedroom',
  };

  return nBeds?.map((nBed) => nBedsTextMap[nBed] ?? `${nBed} Bedrooms`);
};

export const formatNBedsArr = (nBedsArr: string[], nBeds: string): string => {
  if (nBedsArrIsEmptyArrayOrNull(nBedsArr) && nBedsExist(nBeds))
    return mapNBedsToDescription([nBeds]);

  if (isNoPreferenceSelectedForNumBeds(nBedsArr)) return NO_PREFERENCE_TEXT;

  return mapNBedsToDescription(nBedsArr?.sort())?.join(', ') || '---';
};

export const nBedsArrInitialValueBuilder = (
  nBedsArr: string[],
  nBeds: number,
) => {
  return nBedsArrIsEmptyArrayOrNull(nBedsArr) && nBedsExist(nBeds)
    ? [nBeds.toString()]
    : nBedsArr ?? [NO_PREFERENCE_KEY];
};

const getAdditionalOccupants = (
  prospect: FormProspect,
  relationships: Array<Object>,
  existingOccupants?: Array<AdditionalOccupant>,
): Array<AdditionalOccupant> => {
  if (!prospect.adults && !prospect.minors && existingOccupants) {
    return existingOccupants;
  }
  const spouseRelationshipId = (relationships ?? []).find(
    (relationship) => relationship?.text === 'Partner',
  )?.value;
  const spouseAdults = prospect.adults
    ? prospect.adults.filter(
        (adult) => adult?.relationshipId === spouseRelationshipId,
      )
    : [];
  const otherAdults = prospect.adults
    ? prospect.adults.filter(
        (adult) => adult?.relationshipId !== spouseRelationshipId,
      )
    : [];
  const spouseOccupants = spouseAdults.map((spouse) => ({
    type: 'Spouse',
    ...spouse,
    phones: spouse.phones
      ? spouse.phones.map((phone) => ({
          phoneNumber: phone.phoneNumber,
          phoneType: phone.phoneType,
          isPrimary: phone.isPrimary,
          isValidated: phone.isValidated || false,
        }))
      : spouse.phoneNumber
      ? [
          {
            phoneNumber: spouse.phoneNumber,
            phoneType: DEFAULT_PHONE_TYPE,
            isPrimary: true,
            isValidated: false,
          },
        ]
      : undefined,
    emails: spouse.emails
      ? spouse.emails.map((email) => ({
          emailAddress: email.emailAddress,
          emailType: email.emailType,
          isPrimary: email.isPrimary,
          isValidated: email.isValidated || false,
        }))
      : spouse.emailAddress
      ? [
          {
            emailAddress: spouse.emailAddress,
            emailType: DEFAULT_EMAIL_TYPE,
            isPrimary: true,
            isValidated: false,
          },
        ]
      : undefined,
  }));

  const otherAdultsOccupants = otherAdults.map((other) => ({
    type: 'Other Adult',
    ...other,
    phones: other.phones
      ? other.phones.map((phone) => ({
          phoneNumber: phone.phoneNumber,
          phoneType: phone.phoneType,
          isPrimary: phone.isPrimary,
          isValidated: phone.isValidated || false,
        }))
      : other.phoneNumber
      ? [
          {
            phoneNumber: other.phoneNumber,
            phoneType: DEFAULT_PHONE_TYPE,
            isPrimary: true,
            isValidated: false,
          },
        ]
      : undefined,
    emails: other.emails
      ? other.emails.map((email) => ({
          emailAddress: email.emailAddress,
          emailType: email.emailType,
          isPrimary: email.isPrimary,
          isValidated: email.isValidated || false,
        }))
      : other.emailAddress
      ? [
          {
            emailAddress: other.emailAddress,
            emailType: DEFAULT_EMAIL_TYPE,
            isPrimary: true,
            isValidated: false,
          },
        ]
      : undefined,
  }));

  const kidsOccupants = prospect.minors
    ? prospect.minors.map((minor) => ({
        type: 'Minor',
        age: isEmpty(String(minor?.age)) ? null : parseInt(minor?.age, 10),
        birthday:
          minor?.birthday && minor?.birthday !== 'MM/DD/YYYY'
            ? (minor?.birthday)
                .startOf('day') // Time 00:00
                .utc()
                .format('YYYY-MM-DD')
            : null,
        ...omit(['birthday', 'age'], minor),
      }))
    : [];
  return concat(spouseOccupants, concat(kidsOccupants, otherAdultsOccupants));
};

const getPetOccupants = (
  prospect: FormProspect,
  existingPets?: Array<PetOccupant>,
): Array<PetOccupant> => {
  if (!prospect.pets && existingPets) {
    return existingPets;
  }
  return prospect.pets
    ? prospect.pets.map((pet) => ({
        petBreedId: pet.petBreedId === '' ? undefined : pet.petBreedId,
        ...pet,
      }))
    : [];
};

const getDefaultStatus = (prospectStatusList: ProspectStatus[]): string => {
  const listStatus = prospectStatusList.map((prospectStatus) => ({
    value: prospectStatus.id,
    isDefault: prospectStatus.isDefault,
  }));
  return listStatus.filter((s) => s.isDefault === true)[0].value;
};
export const getProspectPreferences = (
  prospect: FormProspect,
  accessibleUnitFlag: boolean = false,
): ProspectPreferences => {
  const sectionProperties = [
    'helpText',
    'leaseTermInMonths',
    'moveInDateFrom',
    'moveInDateScheduled',
    'moveInDateTo',
    'nBaths',
    'nBeds',
    'nBedsArr',
    'nHalfBaths',
    'notWants',
    'preferredFloorPlanId',
    'priceFrom',
    'priceTo',
    'wants',
    'unitType',
    'affordableProgramsArr',
  ].concat(accessibleUnitFlag ? ['prefersUnitAccessible'] : []);

  let preferencesObject: ProspectPreferences = pickAll(
    sectionProperties,
    prospect,
  );

  if (accessibleUnitFlag) {
    preferencesObject.prefersUnitAccessible =
      preferencesObject.prefersUnitAccessible === 'yes';
  }

  preferencesObject = map(setNullToEmpty, preferencesObject);

  const hasUserChosenNoPreferenceForNumBeds =
    Array.isArray(preferencesObject.nBedsArr) &&
    preferencesObject.nBedsArr.includes(NO_PREFERENCE_KEY);

  if (preferencesObject?.unitType === 'conventional') {
    preferencesObject.affordableProgramsArr = [];
  }

  return hasUserChosenNoPreferenceForNumBeds
    ? { ...preferencesObject, nBedsArr: null }
    : preferencesObject;
};

export const getProspectToSubmit = (
  props: {
    prospect: Prospect,
    propertyId: string,
    currentProspect: Prospect,
    prospectStatusList: ProspectStatus[],
    isPost?: boolean,
    relationships: any,
  },
  flags,
) => {
  const {
    prospect,
    propertyId,
    currentProspect,
    prospectStatusList,
    isPost,
    relationships,
  } = props;
  const { accessibleUnit } = flags;

  let prospectToSubmit: Prospect = {
    ...omit(
      [
        'helpText',
        'kids',
        'kidsAmount',
        'kidsChecked',
        'leaseTermInMonths',
        'moveInDateFrom',
        'moveInDateTo',
        'moveInDateScheduled',
        'isUnitAssigned',
        'unitId',
        'nBaths',
        'nBeds',
        'nBedsArr',
        'nHalfBaths',
        'noMoveInDate',
        'notWants',
        'otherAdults',
        'otherAdultsAmount',
        'otherAdultsChecked',
        'pets',
        'petsAmount',
        'petsChecked',
        'preferredFloorPlanId',
        'priceFrom',
        'priceTo',
        'spouse',
        'spouseChecked',
        'wants',
        'prefersUnitAccessible',
        'emailType',
        'phoneType',
        'secondaryEmailAddress',
        'secondaryEmailType',
        'tertiaryEmailAddress',
        'tertiaryEmailType',
        'secondaryPhoneNumber',
        'secondaryPhoneType',
        'tertiaryPhoneNumber',
        'tertiaryPhoneType',
        'unitType',
        'affordableProgramsArr',
      ].concat(isPost ? ['adults', 'minors', 'currentProspectStatus'] : []),
      prospect,
    ),
    referralTypeId: prospect.referralTypeId?.length
      ? prospect.referralTypeId
      : null,
    contactTypeId: prospect.contactTypeId?.length
      ? prospect.contactTypeId
      : null,
    additionalOccupants: getAdditionalOccupants(
      prospect,
      relationships,
      currentProspect?.additionalOccupants,
    ),
    adultsCount:
      prospect.adultsCount === 'default' ? null : prospect.adultsCount,
    minorsCount:
      prospect.minorsCount === 'default' ? null : prospect.minorsCount,
    petsCount: prospect.petsCount === 'default' ? null : prospect.petsCount,
    petOccupants: getPetOccupants(prospect, currentProspect?.petOccupants),
    propertyId,
    prospectPreferences: getProspectPreferences(prospect, accessibleUnit),
    prospectStatusId: prospectStatusList?.length
      ? getDefaultStatus(prospectStatusList)
      : undefined,
  };

  const isValidContact = (v) => v != null && String(v).trim() !== '';

  const emails = [];
  if (isValidContact(prospect.emailAddress)) {
    emails.push({
      value: prospect.emailAddress,
      type: isValidContact(prospect.emailType)
        ? prospect.emailType
        : DEFAULT_EMAIL_TYPE,
      isPrimary: true,
    });
  }
  if (isValidContact(prospect.secondaryEmailAddress)) {
    emails.push({
      value: prospect.secondaryEmailAddress,
      type: isValidContact(prospect.secondaryEmailType)
        ? prospect.secondaryEmailType
        : DEFAULT_EMAIL_TYPE,
      isPrimary: false,
    });
  }
  if (isValidContact(prospect.tertiaryEmailAddress)) {
    emails.push({
      type: isValidContact(prospect.tertiaryEmailType)
        ? prospect.tertiaryEmailType
        : DEFAULT_EMAIL_TYPE,
      value: prospect.tertiaryEmailAddress,
      isPrimary: false,
    });
  }

  const phones = [];
  if (isValidContact(prospect.phoneNumber)) {
    phones.push({
      value: prospect.phoneNumber,
      type: isValidContact(prospect.phoneType)
        ? prospect.phoneType
        : DEFAULT_PHONE_TYPE,
      isPrimary: true,
    });
  }
  if (isValidContact(prospect.secondaryPhoneNumber)) {
    phones.push({
      value: prospect.secondaryPhoneNumber,
      type: isValidContact(prospect.secondaryPhoneType)
        ? prospect.secondaryPhoneType
        : DEFAULT_PHONE_TYPE,
      isPrimary: false,
    });
  }
  if (isValidContact(prospect.tertiaryPhoneNumber)) {
    phones.push({
      value: prospect.tertiaryPhoneNumber,
      type: isValidContact(prospect.tertiaryPhoneType)
        ? prospect.tertiaryPhoneType
        : DEFAULT_PHONE_TYPE,
      isPrimary: false,
    });
  }

  prospectToSubmit.emails = emails;
  prospectToSubmit.phones = phones;

  if (currentProspect) {
    const { id, prospectPreferences } = currentProspect;

    if (prospectPreferences?.unitType === 'conventional') {
      prospectPreferences.affordableProgramsArr = [];
    }

    prospectToSubmit = {
      ...prospectToSubmit,
      id,
      prospectPreferences: {
        ...prospectToSubmit.prospectPreferences,
        id: prospectPreferences.id,
      },
    };
  }

  if (prospectToSubmit.assignedToId === '') {
    prospectToSubmit.assignedToId = null;
  }

  return prospectToSubmit;
};
